import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, ArrowLeftRight, AlertTriangle, Ban, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';

export interface SwapTeamOption {
  registration_id: string;
  team_name: string;
  pool: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  eventId: string;
  division: string;
  teams: SwapTeamOption[];
  onChanged: () => void;
}

interface Preview {
  applied: boolean;
  blocked: boolean;
  block_reason: string | null;
  division: string;
  team_a: { registration_id: string; name: string; pool: string | null; pool_after: string | null; games: number };
  team_b: { registration_id: string; name: string; pool: string | null; pool_after: string | null; games: number };
  games_affected: number;
  broken_constraints: Array<{
    game_id: string; registration_id: string; team_name: string;
    constraint_id: string; constraint_type: string; severity: string; message: string;
  }>;
  new_violations: any[];
  warnings: string[];
}

export default function SwapTeamsDialog({ open, onOpenChange, eventId, division, teams, onChanged }: Props) {
  const [a, setA] = useState<string>('');
  const [b, setB] = useState<string>('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (open) { setA(''); setB(''); setPreview(null); }
  }, [open]);

  const call = async (dryRun: boolean) => {
    const { data: res, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
      body: {
        action: 'swap_teams',
        event_id: eventId,
        registration_id_a: a,
        registration_id_b: b,
        dry_run: dryRun,
      },
    });
    if (error || !res?.ok) {
      throw new Error(error?.message || res?.error?.message || 'Could not work out the swap.');
    }
    return res.data as Preview;
  };

  // The preview is a real dry run — nothing is written until Confirm.
  useEffect(() => {
    if (!open || !a || !b || a === b) { setPreview(null); return; }
    let cancelled = false;
    setLoading(true);
    call(true)
      .then((d) => { if (!cancelled) setPreview(d); })
      .catch((e) => { if (!cancelled) { setPreview(null); toast.error(e.message); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, a, b, eventId]);

  const confirm = async () => {
    setApplying(true);
    try {
      const d = await call(false);
      if (!d.applied) {
        toast.error(d.block_reason || 'The swap was refused.');
        setPreview(d);
        return;
      }
      toast.success(`${d.team_a.name} and ${d.team_b.name} exchanged places — ${d.games_affected} game${d.games_affected === 1 ? '' : 's'} changed hands.`);
      onOpenChange(false);
      onChanged();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setApplying(false);
    }
  };

  const softBreaks = (preview?.broken_constraints ?? []).filter((x) => x.severity !== 'hard');
  const hardBreaks = (preview?.broken_constraints ?? []).filter((x) => x.severity === 'hard');

  const picker = (value: string, onChange: (v: string) => void, exclude: string, label: string) => (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger><SelectValue placeholder="Choose a team" /></SelectTrigger>
        <SelectContent>
          {teams.filter((t) => t.registration_id && t.registration_id !== exclude).map((t) => (
            <SelectItem key={t.registration_id} value={t.registration_id}>
              {t.team_name}{t.pool ? ` · Pool ${t.pool}` : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowLeftRight className="h-5 w-5" /> Swap two teams
          </DialogTitle>
          <DialogDescription>
            They exchange places in {division} — pool, seed and every game. Each game keeps its court
            and its time; only who plays in it changes. Registrations, payments and rosters are untouched.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {picker(a, setA, b, 'Team')}
          {picker(b, setB, a, 'Swaps with')}
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Working out what changes…
          </div>
        )}

        {preview && !loading && (
          <div className="space-y-3 text-sm">
            <div className="rounded-md border p-3 space-y-1">
              <div>
                <strong>{preview.team_a.name}</strong> moves
                {preview.team_a.pool ? ` out of Pool ${preview.team_a.pool}` : ''}
                {preview.team_a.pool_after ? ` into Pool ${preview.team_a.pool_after}` : ''} and ends up with{' '}
                {preview.team_b.games} game{preview.team_b.games === 1 ? '' : 's'}.
              </div>
              <div>
                <strong>{preview.team_b.name}</strong> moves
                {preview.team_b.pool ? ` out of Pool ${preview.team_b.pool}` : ''}
                {preview.team_b.pool_after ? ` into Pool ${preview.team_b.pool_after}` : ''} and ends up with{' '}
                {preview.team_a.games} game{preview.team_a.games === 1 ? '' : 's'}.
              </div>
              <div className="text-muted-foreground">
                {preview.games_affected} game{preview.games_affected === 1 ? '' : 's'} change hands.
              </div>
            </div>

            {hardBreaks.length > 0 && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 space-y-2">
                <div className="flex items-center gap-2 font-medium text-destructive">
                  <Ban className="h-4 w-4" /> Blocked
                </div>
                {hardBreaks.map((x, i) => (
                  <div key={i} className="space-y-1">
                    <p>
                      <strong>{x.team_name}</strong> — {x.message}
                    </p>
                    <a
                      className="inline-flex items-center gap-1 text-xs underline"
                      href={`/admin/events/${eventId}/schedule/requests?request=${x.constraint_id}`}
                    >
                      Open the request <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                ))}
                <p className="text-xs text-muted-foreground">
                  An approved request of this kind is absolute — the same rule the grid applies when you drag a game.
                  Change or deny the request first, then swap.
                </p>
              </div>
            )}

            {(softBreaks.length > 0 || preview.warnings.length > 0) && hardBreaks.length === 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-1">
                <div className="flex items-center gap-2 font-medium text-amber-800">
                  <AlertTriangle className="h-4 w-4" /> Worth knowing
                </div>
                <ul className="list-disc pl-5 text-amber-800">
                  {softBreaks.map((x, i) => (
                    <li key={`b${i}`}>
                      {x.team_name}: {x.message}{' '}
                      <Badge variant="outline" className="ml-1 text-[10px]">{x.severity}</Badge>
                    </li>
                  ))}
                  {preview.warnings
                    .filter((w) => !softBreaks.some((s) => s.message === w))
                    .map((w, i) => <li key={`w${i}`}>{w}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={applying}>Cancel</Button>
          <Button onClick={confirm} disabled={!preview || preview.blocked || applying || loading}>
            {applying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Swap them
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
